/**
 * Helpers shared by the AI batch-commit handlers (`commit_frame`,
 * `commit_tilemap`). They expand palette-indexed RLE strings into 2D color
 * grids and reshape per-cell index buffers into Layer-shaped data.
 *
 * Index 0 is reserved for "transparent" (empty cell). Indices >= 1 select a
 * color: `paletteOverride[i - 1]` if supplied, otherwise the editor's
 * built-in `PALETTE` constant at the same offset (also 1-based).
 */
import type { Color, Layer } from "#canvas/types.ts";
import { COLS, PALETTE, ROWS } from "#puf/constants.ts";
import { expandRle } from "@pixelpuf/schemas/pxpf";

export interface PaletteResolution {
  /** Hex color for index >= 1, or "" when index === 0 / out-of-range. */
  colorFor: (paletteIndex: number) => Color;
}

export function resolvePalette(paletteOverride: readonly string[] | undefined): PaletteResolution {
  const colors: readonly string[] = paletteOverride ?? PALETTE;
  return {
    colorFor: (paletteIndex: number): Color => {
      if (paletteIndex <= 0) return "";
      const offset = paletteIndex - 1;
      const hex = colors[offset];
      return hex ?? "";
    },
  };
}

/** Reshape a flat palette-index buffer (row-major) into a `Color[][]` grid. */
export function indicesToGrid(
  indices: readonly number[],
  rows: number,
  cols: number,
  resolution: PaletteResolution,
): Color[][] {
  if (indices.length !== rows * cols) {
    throw new Error(`grid size ${String(rows)}x${String(cols)} != indices length ${String(indices.length)}`);
  }
  const data: Color[][] = [];
  for (let r = 0; r < rows; r += 1) {
    const row: Color[] = [];
    const rowOffset = r * cols;
    for (let c = 0; c < cols; c += 1) {
      const idx = indices[rowOffset + c] ?? 0;
      row.push(resolution.colorFor(idx));
    }
    data.push(row);
  }
  return data;
}

/**
 * Stitch tiles into a full frame grid. `tilemap` is row-major over the tile
 * grid (canvas-rows / tileSize x canvas-cols / tileSize). Each `tiles[i]` is
 * a `tileSize * tileSize`-cell RLE string in row-major order.
 */
export function tilemapToGrid(
  tiles: readonly string[],
  tilemap: readonly number[],
  tileSize: number,
  rows: number,
  cols: number,
  resolution: PaletteResolution,
): Color[][] {
  if (rows % tileSize !== 0 || cols % tileSize !== 0) {
    throw new Error(`tileSize ${String(tileSize)} must divide canvas ${String(rows)}x${String(cols)}`);
  }
  const tileRows = rows / tileSize;
  const tileCols = cols / tileSize;
  if (tilemap.length !== tileRows * tileCols) {
    throw new Error(
      `tilemap has ${String(tilemap.length)} entries, expected ${String(tileRows * tileCols)} for ${String(rows)}x${
        String(cols)
      } canvas`,
    );
  }

  // Pre-expand each unique tile once; reused tiles share the same buffer.
  const tileBuffers: number[][] = tiles.map((rle) => expandRle(rle, tileSize * tileSize));

  const data: Color[][] = Array.from({ length: rows }, () => Array.from({ length: cols }, () => ""));
  for (let tr = 0; tr < tileRows; tr += 1) {
    for (let tc = 0; tc < tileCols; tc += 1) {
      const slot = tr * tileCols + tc;
      const tileIdx = tilemap[slot] ?? 0;
      const buffer = tileBuffers[tileIdx];
      if (!buffer) {
        throw new Error(`tilemap entry ${String(slot)} -> tile index ${String(tileIdx)} out of range`);
      }
      const baseRow = tr * tileSize;
      const baseCol = tc * tileSize;
      for (let lr = 0; lr < tileSize; lr += 1) {
        const srcRow = lr * tileSize;
        const dstRow = data[baseRow + lr];
        if (!dstRow) continue;
        for (let lc = 0; lc < tileSize; lc += 1) {
          const idx = buffer[srcRow + lc] ?? 0;
          dstRow[baseCol + lc] = resolution.colorFor(idx);
        }
      }
    }
  }
  return data;
}

/**
 * Replace a single layer's data on a frame, preserving every other layer's
 * data. Returns the existing layer list unchanged if `layerId` is missing.
 */
export function replaceLayerData(
  layers: readonly Layer[],
  layerId: string,
  data: Color[][],
): Layer[] | null {
  const index = layers.findIndex((layer) => layer.id === layerId);
  if (index < 0) return null;
  const next = layers.slice();
  const target = layers[index];
  if (!target) return null;
  next[index] = { ...target, data };
  return next;
}

export const DEFAULT_ROWS = ROWS;
export const DEFAULT_COLS = COLS;
